// src/pages/MentionsLegal.jsx
import * as React from 'react';
import {
  Box,
  Container,
  Typography,
  Paper,
  Stack,
} from '@mui/material';

import Navbar from '../components/Navbar';
import Footer from '../components/Footer';

// i18n
import { useI18n } from '../i18n/I18nProvider';
import mentionsI18n from '../i18n/pages/mentionslegalpage.i18n';

const BLUE = '#0466c8';
const BLUE_BG = 'rgba(11,95,255,0.08)';

const Strong = ({ children }) => (
  <Box component="span" sx={{ fontWeight: 800 }}>{children}</Box>
);

export default function MentionsLegalPage() {
  const { t, register } = useI18n();

  React.useEffect(() => {
    register('mentions', mentionsI18n);
  }, [register]);

  // Pour pouvoir masquer les lignes vides proprement
  const legalForm = t('mentions.editor.legalForm');
  const address = t('mentions.editor.address');
  const publicationText = t('mentions.publication.text');

  return (
    <>
      <Navbar />

      {/* HERO */}
      <Box sx={{ bgcolor: BLUE, color: '#fff', py: { xs: 4, md: 6 } }}>
        <Container maxWidth="lg">
          <Typography
            component="h1"
            sx={{
              fontWeight: 900,
              fontSize: { xs: 26, md: 36 },
              lineHeight: 1.15,
              mb: 1,
            }}
          >
            {t('mentions.hero.title')}
          </Typography>
          <Typography sx={{ opacity: 0.95, fontSize: { xs: 15.5, md: 17 } }}>
            {t('mentions.hero.desc')}
          </Typography>
        </Container>
      </Box>

      {/* CONTENU */}
      <Box sx={{ bgcolor: '#fff' }}>
        <Container maxWidth="lg" sx={{ py: { xs: 3, md: 4 } }}>
          <Paper
            variant="outlined"
            sx={{
              p: { xs: 2, md: 2.5 },
              borderRadius: 3,
              borderColor: '#e8efe9',
            }}
          >
            {/* ÉDITEUR DU SITE */}
            <Box sx={{ mb: 2.5, p: 1.5, borderRadius: 2, bgcolor: BLUE_BG }}>
              <Typography sx={{ fontWeight: 800, mb: 0.5 }}>
                {t('mentions.editor.title')}
              </Typography>
              <Stack spacing={0.5} sx={{ color: 'text.secondary' }}>
                <Typography>
                  <Strong>{t('mentions.editor.corporateNameLabel')}</Strong>{' '}
                  {t('mentions.editor.corporateName')}
                </Typography>

                {legalForm && (
                  <Typography>
                    <Strong>{t('mentions.editor.legalFormLabel')}</Strong>{' '}
                    {legalForm}
                  </Typography>
                )}

                {address && (
                  <Typography>
                    <Strong>{t('mentions.editor.addressLabel')}</Strong>{' '}
                    {address}
                  </Typography>
                )}

                <Typography>
                  <Strong>{t('mentions.editor.siretLabel')}</Strong>{' '}
                  {t('mentions.editor.siret')}
                </Typography>
                <Typography>
                  <Strong>{t('mentions.editor.emailLabel')}</Strong>{' '}
                  <a href={`mailto:${t('mentions.editor.email')}`}>
                    {t('mentions.editor.email')}
                  </a>
                </Typography>
              </Stack>
            </Box>

            {/* RESPONSABLE DE PUBLICATION */}
            {publicationText && (
              <Box sx={{ mb: 2.5 }}>
                <Typography sx={{ fontWeight: 800, mb: 0.5 }}>
                  {t('mentions.publication.title')}
                </Typography>
                <Typography sx={{ color: 'text.secondary' }}>
                  {publicationText}
                </Typography>
              </Box>
            )}

            {/* HÉBERGEUR */}
            <Box sx={{ mb: 2.5 }}>
              <Typography sx={{ fontWeight: 800, mb: 0.5 }}>
                {t('mentions.hosting.title')}
              </Typography>
              <Stack spacing={0.5} sx={{ color: 'text.secondary' }}>
                <Typography>
                  <Strong>{t('mentions.hosting.nameLabel')}</Strong>{' '}
                  {t('mentions.hosting.name')}
                </Typography>
                <Typography>
                  <Strong>{t('mentions.hosting.addressLabel')}</Strong>{' '}
                  {t('mentions.hosting.address')}
                </Typography>
                <Typography>
                  <Strong>{t('mentions.hosting.websiteLabel')}</Strong>{' '}
                  <a
                    href={t('mentions.hosting.website')}
                    target="_blank"
                    rel="noreferrer"
                  >
                    {t('mentions.hosting.website')}
                  </a>
                </Typography>
              </Stack>
            </Box>

            {/* PROPRIÉTÉ INTELLECTUELLE */}
            <Box sx={{ mb: 2.5 }}>
              <Typography sx={{ fontWeight: 800, mb: 0.5 }}>
                {t('mentions.ip.title')}
              </Typography>
              <Typography sx={{ color: 'text.secondary' }}>
                {t('mentions.ip.text')}
              </Typography>
            </Box>

            {/* DONNÉES PERSONNELLES */}
            <Box>
              <Typography sx={{ fontWeight: 800, mb: 0.5 }}>
                {t('mentions.data.title')}
              </Typography>
              <Typography sx={{ color: 'text.secondary' }}>
                {t('mentions.data.text')}
              </Typography>
            </Box>
          </Paper>
        </Container>
      </Box>

      <Footer />
    </>
  );
}
